import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import {SafeDigsClientBase, isAxiosError, throwException} from './SafeDigsClientBase';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';

export class SafeDigTaskClient extends SafeDigsClientBase {
    private instance: AxiosInstance;
    private baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(configuration: MNSwagClientConfiguration, baseUrl?: string, instance?: AxiosInstance) {

        super(configuration);

        this.instance = instance ? instance : axios.create();

        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl('');
    }

    /**
     * @return Success
     */
    get(safeDigId: number, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDigTask/safedigtask?id={safeDigTaskId}';
        if(safeDigId === undefined || safeDigId === null) {
            throw new Error('The parameter \'safeDigTaskId\' must be defined.');
        }
        url_ = url_.replace('{safeDigTaskId}', encodeURIComponent('' + safeDigId));
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGet(_response);
        });
    }

    protected processGet(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @param body (optional)
     * @return Success
     */
    update(body: UpdateSafeDigTaskRequest | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDigTask/UpdateTask';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'PUT',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processUpdate(_response);
        });
    }

    protected processUpdate(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            let resultData400 = _responseText;
            result400 = resultData400 !== undefined ? resultData400 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result400);

        } else if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 409) {
            const _responseText = response.data;
            let result409: any = null;
            let resultData409 = _responseText;
            result409 = resultData409 !== undefined ? resultData409 : null as any;

            return throwException('Conflict', status, _responseText, _headers, result409);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @return Success
     */
    getAll(safeDigId?: number, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDigTask/GetAll';
        if(safeDigId !== undefined) {
            url_ = this.baseUrl + '/SafeDigTask/GetAll?safeDigsId={safeDigId}';
            url_ = url_.replace('{safeDigId}', encodeURIComponent('' + safeDigId));
        }
        url_ = url_.replace(/[?&]$/, '');

        let options_: AxiosRequestConfig = {
            method: 'GET',
            url: url_,
            headers: {
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processGetAll(_response);
        });
    }

    protected processGetAll(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }

    /**
     * @param body (optional)
     * @return Success
     */
    post(body: CreateSafeDigTaskRequest | undefined, cancelToken?: CancelToken | undefined): Promise<any> {
        let url_ = this.baseUrl + '/SafeDigTask/CreateTask';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: AxiosRequestConfig = {
            data: content_,
            method: 'POST',
            url: url_,
            headers: {
                'Content-Type': 'application/json-patch+json',
                'Accept': 'text/plain'
            },
            cancelToken
        };

        return this.transformOptions(options_).then(transformedOptions_ => {
            return this.instance.request(transformedOptions_);
        }).catch((_error: any) => {
            if(isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        }).then((_response: AxiosResponse) => {
            return this.processPost(_response);
        });
    }

    protected processPost(response: AxiosResponse): Promise<any> {
        const status = response.status;
        let _headers: any = {};
        if(response.headers && typeof response.headers === 'object') {
            for(let k in response.headers) {
                if(response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if(status === 400) {
            const _responseText = response.data;
            let result400: any = null;
            let resultData400 = _responseText;
            result400 = resultData400 !== undefined ? resultData400 : null as any;

            return throwException('Bad Request', status, _responseText, _headers, result400);

        } else if(status === 404) {
            const _responseText = response.data;
            let result404: any = null;
            let resultData404 = _responseText;
            result404 = resultData404 !== undefined ? resultData404 : null as any;

            return throwException('Not Found', status, _responseText, _headers, result404);

        } else if(status === 409) {
            const _responseText = response.data;
            let result409: any = null;
            let resultData409 = _responseText;
            result409 = resultData409 !== undefined ? resultData409 : null as any;

            return throwException('Conflict', status, _responseText, _headers, result409);

        } else if(status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200 = _responseText;
            result200 = resultData200 !== undefined ? resultData200 : null as any;

            return Promise.resolve<any>(result200);

        } else if(status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }
        return Promise.resolve<any>(null as any);
    }
};

export class UpdateSafeDigTaskRequest implements IUpdateSafeDigTaskRequest {
    safeDigTasksId: number | undefined;
    taskStatus: number | undefined;
    taskSubType: number | undefined;
    safeDigsId: number | undefined;
    taskType: number | undefined;
    comment: string | undefined;
    dueDate: Date | undefined;
    user: string | undefined;

    constructor(data?: IUpdateSafeDigTaskRequest) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.safeDigTasksId = _data['safeDigTasksId'];
            this.taskStatus = _data['taskStatus'];
            this.taskSubType = _data['taskSubType'];
            this.safeDigsId = _data['safeDigsId'];
            this.taskType = _data['taskType'];
            this.comment = _data['comment'];
            this.dueDate = _data['dueDate'] ? new Date(_data['dueDate'].toISOString()) : undefined as any;
            this.user = _data['user'];
        }
    }

    static fromJS(data: any): UpdateSafeDigTaskRequest {
        data = typeof data === 'object' ? data : {};
        let result = new UpdateSafeDigTaskRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['safeDigTasksId'] = this.safeDigTasksId;
        data['taskStatus'] = this.taskStatus;
        data['taskSubType'] = this.taskSubType;
        data['safeDigsId'] = this.safeDigsId;
        data['taskType'] = this.taskType;
        data['comment'] = this.comment;
        data['dueDate'] = this.dueDate ? this.dueDate.toISOString() : undefined as any;
        data['user'] = this.user;
        return data;
    }
};

export class CreateSafeDigTaskRequest implements ICreateSafeDigTaskRequest {
    taskSubType: number | undefined;
    safeDigsId: number | undefined;
    taskType: number | undefined;
    comment: string | undefined;
    dueDate: Date | undefined;
    taskStatus: number | undefined;
    user: string | undefined;

    constructor(data?: ICreateSafeDigTaskRequest) {
        if(data) {
            for(var property in data) {
                if(data.hasOwnProperty(property)) {
                    (this as any)[property] = (data as any)[property];
                }
            }
        }
    }

    init(_data?: any) {
        if(_data) {
            this.taskSubType = _data['taskSubType'];
            this.safeDigsId = _data['safeDigsId'];
            this.taskType = _data['taskType'];
            this.comment = _data['comment'];
            this.dueDate = _data['dueDate'] ? new Date(_data['dueDate'].toISOString()) : undefined as any;
            this.taskStatus = _data['taskStatus'];
            this.user = _data['user'];
        }
    }

    static fromJS(data: any): CreateSafeDigTaskRequest {
        data = typeof data === 'object' ? data : {};
        let result = new CreateSafeDigTaskRequest();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['taskSubType'] = this.taskSubType;
        data['safeDigsId'] = this.safeDigsId;
        data['taskType'] = this.taskType;
        data['comment'] = this.comment;
        data['dueDate'] = this.dueDate ? this.dueDate.toISOString() : undefined as any;
        data['taskStatus'] = this.taskStatus;
        data['user'] = this.user;
        return data;
    }
}

export interface ICreateSafeDigTaskRequest {
    taskSubType: number | undefined;
    safeDigsId: number | undefined;
    taskType: number | undefined;
    comment: string | undefined;
    dueDate: Date | undefined;
    taskStatus: number | undefined;
    user: string | undefined;
};

export interface IUpdateSafeDigTaskRequest {
    safeDigTasksId: number | undefined;
    taskStatus: number | undefined;
    taskSubType: number | undefined;
    safeDigsId: number | undefined;
    taskType: number | undefined;
    comment: string | undefined;
    dueDate: Date | undefined;
    user: string | undefined;
};